import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {PurchaseHistorySingleItemsDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import {
    DataGridColumnAlignments,
    DataGridColumnTypes,
    useDataGridSavedState,
} from "../../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import PurchaseSerialNumberChips from "../../../../shared/serial-number-chips";

const saveConfig = {name: 'PurchaseHistorySingleProductsDataGrid', version: 2};

/**
 * Products tab for purchase history single — read-only lines + barcode serial chips.
 */
const PurchaseHistorySingleProductsSection = ({items = [], loading = false}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.historySingle.products'});

    const columns = useMemo(() => /** @type {DataGridColumn[]} */ ([
        {
            title: t('columns.partNo'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.partNo,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.vendorPartNo'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.vendorPartNo,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.description'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.description,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            width: 180,
        },
        {
            title: t('columns.comments'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.comments,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.orderQty'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.orderQty,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.right,
        },
        {
            title: t('columns.cost'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.cost,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.right,
        },
        {
            title: t('columns.extendedCost'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.extendedCost,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.right,
        },
        {
            title: t('columns.serials'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.serials,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            width: 240,
        },
    ]), [t]);

    const [gridColumns, , , density] = useDataGridSavedState({columns, ...saveConfig});

    const rows = useMemo(() => items.map((item) => {
        const extended = item.extendedCost != null
            ? item.extendedCost
            : (Number(item.orderQty) || 0) * (Number(item.cost) || 0);

        return {
            key: item.id,
            data: item,
            cells: {
                [PurchaseHistorySingleItemsDataGridCellKeys.partNo]: item.partNo,
                [PurchaseHistorySingleItemsDataGridCellKeys.vendorPartNo]: item.vendorPartNo,
                [PurchaseHistorySingleItemsDataGridCellKeys.description]: item.description,
                [PurchaseHistorySingleItemsDataGridCellKeys.comments]: item.comments ?? '',
                [PurchaseHistorySingleItemsDataGridCellKeys.orderQty]: item.orderQty,
                [PurchaseHistorySingleItemsDataGridCellKeys.cost]: formatMoney(item.cost),
                [PurchaseHistorySingleItemsDataGridCellKeys.extendedCost]: formatMoney(extended),
                [PurchaseHistorySingleItemsDataGridCellKeys.serials]: (
                    <PurchaseSerialNumberChips serials={item.serials ?? []}/>
                ),
            },
        };
    }), [items]);

    return (
        <PartnerDataGrid
            {...saveConfig}
            initialLayoutState={{columns}}
            columns={gridColumns}
            rows={rows}
            hideFooterActions
            hideExporting
            state={{loading: {state: loading}, density}}
        />
    );
};

export default PurchaseHistorySingleProductsSection;
